import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Cookies } from 'react-cookie';
import { ModalIsland, Button, Typography } from '@saviynt/design-system';
import * as logoutUtil from 'ui-common/src/utils/logout';
import { USER_SESSION } from 'ui-common/src/constants';

import './SessionTimeoutModal.css';

const cookies = new Cookies();

const SESSION_EXTEND_API = '/ECM/session/extend';

function getTimeoutInfoFromCookie() {
  const info = cookies.get(USER_SESSION);

  if (!info) {
    return { featureEnabled: false };
  }

  if (!(info.timeoutMins && info.promptMins && info.lastRefreshedAt)) {
    return { featureEnabled: true, error: true };
  }

  // Give users at least 5 minutes without a timeout prompt to allow
  // them to recover from a bad configuration.
  if (info.timeoutMins - info.promptMins < 5) {
    info.promptMins = Math.max(info.timeoutMins - 5, 0);
  }

  const ellapsedMillis = new Date().getTime() - info.lastRefreshedAt;
  const ellapsedSeconds = Math.floor(ellapsedMillis / 1000);

  const timeoutSeconds = info.timeoutMins * 60;
  const promptMinutes = info.timeoutMins - info.promptMins;
  const promptSeconds = promptMinutes * 60;

  return {
    featureEnabled: true,
    timeUntilModal: Math.max(promptSeconds - ellapsedSeconds, 0),
    shouldShowModal: ellapsedSeconds >= promptSeconds,
    timeUntilLogout: Math.max(timeoutSeconds - ellapsedSeconds, 0),
    shouldLogOut: ellapsedSeconds >= timeoutSeconds,
  };
}

const SessionTimeoutModal = () => {
  const [timeUntilLogout, setTimeUntilLogout] = useState(0);
  const [shouldShowModal, setShouldShowModal] = useState(false);

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isExtending, setIsExtending] = useState(false);

  const sessionTimeout = () => {
    setIsLoggingOut(true);
    logoutUtil.logout(logoutUtil.LogoutTrigger.sessionTimeout);
  };

  const userLogout = () => {
    setIsLoggingOut(true);
    logoutUtil.logout(logoutUtil.LogoutTrigger.userInitiated);
  };

  const extendSession = async () => {
    // Prevents extending session on a logout action.
    if (isLoggingOut) return;

    setIsExtending(true);

    const response = await fetch(SESSION_EXTEND_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    setIsExtending(false);

    if (!response.ok) {
      // If something went wrong when trying to extend the session,
      // we should log out for security reasons.
      userLogout();
    }
  };

  useEffect(() => {
    let timeoutId = 0;
    let countdownId = 0;

    const updateInfo = () => {
      const timeoutInfo = getTimeoutInfoFromCookie();

      if (!timeoutInfo.featureEnabled || timeoutInfo.error) {
        setShouldShowModal(false);
      } else {
        setShouldShowModal(timeoutInfo.shouldShowModal);
        setTimeUntilLogout(timeoutInfo.timeUntilLogout);
      }

      return timeoutInfo;
    };

    const refreshTimers = () => {
      clearTimeout(timeoutId);
      clearInterval(countdownId);

      const timeoutInfo = updateInfo();

      if (!timeoutInfo.featureEnabled) {
        return;
      }

      if (timeoutInfo.shouldLogOut) {
        sessionTimeout();
      } else if (timeoutInfo.shouldShowModal) {
        // Update time remaining before timeout
        countdownId = setInterval(() => {
          if (updateInfo().shouldLogOut) {
            refreshTimers();
          }
        }, 250); // Update 4x per second
      } else if (timeoutInfo.timeUntilModal >= 0) {
        // Timer to show modal
        timeoutId = setTimeout(() => {
          refreshTimers();
        }, timeoutInfo.timeUntilModal * 1000); // in milliseconds
      } else {
        // Error: log out
        sessionTimeout();
      }
    };

    refreshTimers();

    return () => {
      clearTimeout(timeoutId);
      clearInterval(countdownId);
    };
  }, []);

  const minutes = Math.floor(timeUntilLogout / 60);
  const seconds = timeUntilLogout % 60;
  const isOpen = shouldShowModal && !isLoggingOut && !isExtending;

  return (
    <ModalIsland
      kind="critical"
      isOpen={isOpen}
      onClose={extendSession}
      hasPrivacyOverlay
      HeaderComp={
        <>
          <Typography kind="h2" className="SessionTimeoutModal-title">
            <FormattedMessage
              id="SessionTimeoutModal.title"
              defaultMessage="You will be logged out soon due to inactivity"
            />
          </Typography>
          <Typography kind="display3" className="SessionTimeoutModal-timer">
            {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
          </Typography>
        </>
      }
      primaryButton={
        <Button kind="outlined" size="medium" onClick={extendSession}>
          <FormattedMessage
            id="SessionTimeoutModal.continueSession"
            defaultMessage="Continue Session"
          />
        </Button>
      }
      secondaryButton={
        <Button kind="ghost" size="medium" onClick={userLogout}>
          <FormattedMessage id="RequestHome.Logout" defaultMessage="Log Out" />
        </Button>
      }
    />
  );
};

export default SessionTimeoutModal;
